import { Box, InputAdornment, Popover, Stack, TextField, Tooltip } from "@mui/material";
import { useRef } from "react";
import { Avatar } from "@src/appV2/lib/Avatar";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";

import { Icon } from "@src/appV2/redesign/components/Icon";
import { Text } from "@src/appV2/redesign/components/Text";
import { AccountSwitcherMenuWorkplaceDesktop } from "./AccountSwitcherDropdownWorkplace";
import { useAccountSwitcher } from "../../Shared/AccountSwitcher/useAccountSwitcher";
import { AccountSwitcherDropdownAdminDesktop } from "./AccountSwitcherDropdownAdmin";

export function AccountSwitcherDesktop() {
  const anchorRef = useRef<HTMLDivElement>(null);
  const {
    switcherTitle,
    switcherSubtitle,
    displayAdminSwitcher,
    dropdownVisible,
    onAdminSearchBarEntered,
    onDropdownButtonClicked,
    onDropdownMouseLeave,
    workerDetails,
    workplaceDetails,
    onSearchChanged,
    onUserClicked,
    onAdminClicked,
  } = useAccountSwitcher();

  return (
    <Box ref={anchorRef}>
      {displayAdminSwitcher ? (
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search agents or workplaces..."
          autoComplete="off"
          sx={(theme) => ({
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background?.inverted?.muted,
              color: theme.palette.navbar?.itemContentSelected,
              paddingRight: 0,
              "& fieldset": {
                borderColor: theme.palette.button?.outline?.inverted?.normal?.borderColor,
              },
              "&:hover fieldset": {
                borderColor: theme.palette.button?.outline?.inverted?.hover?.borderColor,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.button?.outline?.inverted?.hover?.borderColor,
              },
            },
            "& .MuiOutlinedInput-input": {
              color: theme.palette.navbar?.itemContentSelected,
              "&::placeholder": {
                color: theme.palette.navbar?.itemContent,
                opacity: 1,
              },
            },
            width: "300px",
          })}
          onClick={onAdminSearchBarEntered}
          onChange={(event) => {
            onSearchChanged(event.target.value);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ marginLeft: 0, paddingLeft: 0 }}>
                <Tooltip title="Switch To Admin">
                  <span>
                    <IconButton
                      variant="linkInverted"
                      iconType="person-male-formal"
                      sx={(theme) => ({
                        borderRadius: 0,
                        borderLeft: `1px solid ${theme.palette.button?.outline?.inverted?.normal?.borderColor}`,
                        paddingX: 3,
                        height: "100%",
                        "&:hover": {
                          backgroundColor: theme.palette.background?.inverted?.muted,
                        },
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAdminClicked();
                      }}
                    />
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      ) : (
        <Button
          variant="linkInverted"
          sx={{ width: "250px", minHeight: "50px" }}
          onClick={onDropdownButtonClicked}
        >
          <Stack direction="row" alignItems="center" gap={3} overflow="hidden" paddingY={1}>
            <Avatar
              name={switcherTitle}
              size="small"
              sx={(theme) => ({
                bgcolor: theme.palette.navbar?.avatarBackground,
                color: theme.palette.navbar?.avatarText,
                flexShrink: 0,
              })}
            />
            <Stack direction="column" flex={1} minWidth={0}>
              <Text
                variant="body1"
                textAlign="left"
                color={(theme) => theme.palette.navbar?.userName}
                noWrap
              >
                {switcherTitle}
              </Text>
              {switcherSubtitle && (
                <Text variant="caption" textAlign="left" noWrap>
                  {switcherSubtitle}
                </Text>
              )}
            </Stack>
            <Icon
              type="caret-bottom"
              sx={(theme) => ({
                color: theme.palette.button?.outline.inverted.hover.color,
                flexShrink: 0,
              })}
            />
          </Stack>
        </Button>
      )}

      <Popover
        open={dropdownVisible}
        anchorEl={anchorRef.current}
        onClose={onDropdownMouseLeave}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              minWidth: anchorRef.current?.offsetWidth,
              maxHeight: "60vh",
              overflowY: "auto",
              background: theme.palette.navbar?.gradient,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.4)",
              padding: 3,
            }),
            onMouseLeave: onDropdownMouseLeave,
          },
        }}
      >
        {displayAdminSwitcher ? (
          <AccountSwitcherDropdownAdminDesktop
            workerDetails={workerDetails}
            workplaceDetails={workplaceDetails}
            onUserClicked={onUserClicked}
          />
        ) : (
          <AccountSwitcherMenuWorkplaceDesktop
            workplaceDetails={workplaceDetails}
            onUserClicked={onUserClicked}
          />
        )}
      </Popover>
    </Box>
  );
}
